<template>
  <div class="pt-6">
    <!-- Affichage des détails de commande -->
    <div v-if="selectedOrderId" class="w-full py-4 lg:pl-6">
      <!-- Header avec bouton retour -->
      <div class="mt-4 mb-6 flex flex-col gap-2 lg:mt-2">
        <button
          @click="handleGoBack"
          class="flex items-center gap-2 text-black hover:text-black transition-colors self-start"
        >
          <i class="fas fa-arrow-left"></i>
          <span class="underline">{{
            isFromGroupage ? "Retour au groupage" : "Retour aux commandes"
          }}</span>
        </button>
      </div>

      <!-- Message d'erreur -->
      <div
        v-if="errorMessage"
        class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
      >
        <p class="text-red-800 font-medium">
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ errorMessage }}
        </p>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-8">
        <i class="fas fa-spinner fa-spin text-2xl text-black"></i>
        <p class="mt-2 text-black">Chargement des détails de la commande...</p>
      </div>

      <!-- Contenu principal  -->
      <div
        v-if="!isLoading && !errorMessage && orderData"
        class="flex flex-col"
      >
        <!-- Bloc Détails de la commande -->
        <OrderDetailsSection
          :orderId="selectedOrderId"
          :loading="isLoading"
          :collapsible="true"
          :disableAction="disableAction"
          @collapsed="isOrderDetailsCollapsed = $event"
          @replacement-confirmation="replacementConfirmation = $event"
          class="mb-6"
        />

        <div
          v-if="isFreeShippingOrder"
          class="mb-6 rounded-lg border border-emerald-300 bg-emerald-50 p-4"
        >
          <p class="font-semibold text-emerald-900">
            <i class="fas fa-gift mr-2"></i>Livraison gratuite
          </p>
          <p class="mt-1 text-sm text-emerald-800">
            Cette commande est expédiée gratuitement par votre boutique. Aucun identifiant de livraison eFashion n'est requis.
          </p>
        </div>

        <!-- Sections d'informations -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <!-- Section Informations client -->
          <ClientInfo
            :orderId="selectedOrderId"
            :collapsible="true"
            @invoice-uploaded="handleInvoiceUploaded"
            @collapsed="isClientInfoCollapsed = $event"
          />

          <!-- Section Adresse livraison -->
          <AddressDetails
            :orderId="selectedOrderId"
            :collapsible="true"
            :carrier-message="carrierMessage"
            :has-existing-labels="hasExistingLabels"
            @collapsed="isAddressDetailsCollapsed = $event"
          />
        </div>

        <!-- Zone admin (impersonation EFP uniquement) -->
        <div
          v-if="authStore.isImpersonating"
          class="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4 items-start"
        >
          <OrderAdminZone
            class="min-w-0"
            :orderId="selectedOrderId"
            :orderDetail="orderDetailResponse"
          />
          <OrderAdminAddresses
            panel="livraison"
            :orderDetail="orderDetailResponse"
          />
          <OrderAdminAddresses
            panel="facturation"
            :orderDetail="orderDetailResponse"
          />
        </div>

        <!-- Commentaires de la commande -->
        <div
          v-if="orderDetailResponse?.commentaires != ''"
          class="bg-white border border-gray-200 rounded-lg p-6 mb-6"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-24">
              <p class="text-sm font-bold text-black">Commentaire :</p>
            </div>
            <div class="flex-1">
              <p class="text-sm text-black whitespace-pre-wrap">
                {{ orderDetailResponse?.commentaires || "" }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="shouldShowGroupageReadiness"
          class="flex flex-wrap lg:flex-nowrap gap-6 mb-6"
        >
          <!-- Avancement groupage (Fedex / Chrono multi / ...) -->
          <div
            v-if="shouldShowGroupageReadiness"
            class="flex-1 min-w-[300px] bg-blue-50 border border-blue-200 rounded-lg p-3"
          >
            <h4 class="text-base font-semibold text-black mb-1">
              Avancement du groupage
            </h4>
            <p class="text-blue-800 text-sm font-medium">
              {{ groupageReadiness?.countReady || 0 }} prête(s) sur
              {{ groupageReadiness?.countTotal || 0 }}.
            </p>
            <p
              v-if="groupageReadiness && groupageReadiness.countTotal > 0"
              class="text-blue-800 text-xs mt-1"
            >
              Vous pourrez télécharger vos étiquettes lorsque toutes les
              commandes du groupage seront prêtes.<br /><br />
              Une notification par email vous sera envoyée dès que les
              étiquettes seront générées.<br /><br />
              Celles-ci sont produites automatiquement toutes les heures, entre
              8h et 14h.
            </p>
          </div>
        </div>

        <!-- Rappel douanier visible directement sur la fiche commande,
             notamment lorsque la destination force le groupage. -->
        <div
          v-if="orderDetailResponse?.outsideEuShippingZone === true"
          class="mb-6 rounded-lg border-2 border-amber-400 bg-amber-50 p-4"
        >
          <p class="text-sm font-semibold text-amber-950">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Destination hors Union européenne : pensez à coller la facture
            commerciale sur le colis avant de le remettre au transporteur.
            Sans facture visible, la douane peut refuser ou retourner le colis.
          </p>
        </div>

        <!-- Grille Transporteur et Étiquettes -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- Section Télécharger vos étiquettes -->
          <div class="space-y-4">
            <div
              v-if="canAdminGenerateUsFinalLabels"
              class="rounded-lg border border-indigo-200 bg-indigo-50 p-4"
            >
              <h4 class="mb-2 text-base font-semibold text-black">
                Étiquette finale USA
              </h4>
              <p class="mb-3 text-sm text-indigo-800">
                Le pickup eFashion est effectué. Cette action génère les
                étiquettes uniquement pour cette commande.
              </p>
              <button
                type="button"
                class="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:bg-gray-400"
                :disabled="isGeneratingAdminUsLabels"
                @click="generateAdminUsFinalLabels"
              >
                <i
                  :class="isGeneratingAdminUsLabels ? 'fas fa-spinner fa-spin' : 'fas fa-tag'"
                  class="mr-2"
                ></i>
                {{
                  isGeneratingAdminUsLabels
                    ? "Génération..."
                    : hasExistingLabels
                      ? "Régénérer pour cette commande"
                      : "Générer pour cette commande"
                }}
              </button>
            </div>

            <!-- Imprimer détails des colis -->
            <div
              v-if="shouldShowPackageDetails"
              class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
            >
              <h4 class="text-base font-semibold text-black mb-2">
                {{ isOwnCarrierDirectOrder
                  ? "Tickets eFashion et bordereau d’enlèvement"
                  : "Imprimer étiquettes de groupage" }}
              </h4>
              <div class="flex items-center gap-3">
                <button
                  @click="handleGeneratePackageDetails"
                  :disabled="
                    isGeneratingPackageDetails || isSavingParcelDimensions
                  "
                  class="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <i
                    v-if="
                      !isGeneratingPackageDetails && !isSavingParcelDimensions
                    "
                    class="fas fa-file-download text-xs"
                  ></i>
                  <i v-else class="fas fa-spinner fa-spin text-xs"></i>
                  <span>{{
                    isGeneratingPackageDetails || isSavingParcelDimensions
                      ? "Generation..."
                      : "Telecharger"
                  }}</span>
                </button>
              </div>
              <p v-if="isOwnCarrierDirectOrder" class="text-blue-800 text-xs font-medium mt-2">
                Collez un ticket sur chaque colis. Faites signer le bordereau par le chauffeur et conservez-le.
              </p>
              <p v-else class="text-blue-800 text-xs font-medium mt-2">
                <strong
                  >Avant téléchargement : facture + dimensions/poids des
                  colis.</strong
                >
                Puis imprimez les étiquettes, collez-les sur vos colis et
                remettez-les à Easy Express.
              </p>
            </div>

            <!-- Zone Étiquettes -->
            <div v-if="shouldShowLabelZone">
              <div
                v-if="canShowLabelGenerateCta"
                class="bg-white border border-gray-200 rounded-lg p-6 mb-3"
              >
                <h4 class="text-lg font-semibold text-black mb-4">
                  Télécharger vos étiquettes
                  {{ orderCarrierLibelle || "" }}
                </h4>
                <p class="mb-3 text-sm text-gray-700">
                  Renseignez la facture, les dimensions et le poids des colis
                  pour générer vos étiquettes.
                </p>
                <button
                  type="button"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  :disabled="isCheckingLabels"
                  @click="handleRegenerateLabels"
                >
                  <i class="fas fa-tags mr-2"></i>
                  Générer vos étiquettes
                </button>
              </div>

              <div
                v-if="canShowAdminRegenerateLabelsCta"
                class="mb-3 flex justify-end"
              >
                <button
                  type="button"
                  class="px-3 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  :disabled="isCheckingLabels || isAdminRegeneratingLabels"
                  @click="askAdminRegenerateNormalLabels"
                >
                  <i
                    :class="
                      isAdminRegeneratingLabels
                        ? 'fas fa-spinner fa-spin'
                        : 'fas fa-sync-alt'
                    "
                    class="mr-2"
                  ></i>
                  {{
                    isAdminRegeneratingLabels
                      ? "Préparation..."
                      : "Régénérer les étiquettes"
                  }}
                </button>
              </div>

              <!-- Colissimo -->
              <LabelColissimo
                v-if="labelModule === 'colissimo' && hasExistingLabels"
                :key="`colissimo-${selectedOrderId}`"
                :order-id="selectedOrderId"
                :order-status="Number(orderData.statut) || 0"
                :nb-colis-max="orderData.packages || 1"
                :poids-total="orderData.weight || 0"
              />

              <!-- DPD -->
              <LabelDPD
                v-else-if="labelModule === 'dpd' && hasExistingLabels"
                :key="`dpd-${selectedOrderId}`"
                :order-id="selectedOrderId"
                :order-status="Number(orderData.statut) || 0"
                :carrier-name="orderCarrierLibelle || 'DPD'"
                :nb-colis-max="orderData.packages || 1"
                :poids-total="orderData.weight || 0"
              />

              <!-- UPS -->
              <LabelUPS
                v-else-if="labelModule === 'ups' && hasExistingLabels"
                :key="`ups-${selectedOrderId}`"
                :order-id="selectedOrderId"
                :order-status="Number(orderData.statut) || 0"
                :carrier-name="orderCarrierLibelle || 'UPS'"
                :nb-colis-max="orderData.packages || 1"
                :poids-total="orderData.weight || 0"
              />

              <!-- Easy -->
              <LabelEasy
                v-else-if="labelModule === 'easy' && hasExistingLabels"
                :key="`easy-${selectedOrderId}`"
                :order-id="selectedOrderId"
                :order-status="Number(orderData.statut) || 0"
                :carrier-name="orderCarrierLibelle || 'Transporteur'"
                :id-livraison="
                  orderData.id_livraison ? Number(orderData.id_livraison) : null
                "
                :nb-colis-max="orderData.packages || 1"
                :poids-total="orderData.weight || 0"
              />

              <!-- Free Shipping -->
              <LabelFreeShipping
                v-else-if="labelModule === 'freeShipping'"
                :key="`freeshipping-${selectedOrderId}`"
                :order-id="selectedOrderId"
                :order-status="Number(orderData.statut) || 0"
              />

            </div>
          </div>

          <!-- Colonne droite -->
          <div class="space-y-4">
            <!-- Information du pickup/dépose -->
            <div
              v-if="orderDetailResponse?.pickupColisInfo?.length"
              class="bg-white border border-gray-200 rounded-lg p-3"
            >
              <h4 class="text-sm font-semibold text-black mb-2">
                Information du pickup/dépose
              </h4>
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b">
                    <th class="text-left py-1">Nb colis</th>
                    <th class="text-left py-1">Réceptionnaire</th>
                    <th class="text-left py-1">Date pickup/déposée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="colis in orderDetailResponse.pickupColisInfo"
                    :key="colis.id_colis_pickup"
                    class="border-b"
                  >
                    <td class="py-1">
                      {{ orderDetailResponse?.nb_colis ?? colis.nb_colis }}
                    </td>
                    <td class="py-1">{{ colis.nom_reception }}</td>
                    <td class="py-1">
                      {{ colis.date_creation || "—" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Colis scannés -->
            <div
              v-if="shouldShowParcelScanInfo"
              class="bg-white border border-gray-200 rounded-lg p-3"
            >
              <p class="text-sm font-semibold text-black">
                Colis scannes :
                {{ orderDetailResponse?.parcelScanInfo?.scannedCount || 0 }} /
                {{ orderDetailResponse?.parcelScanInfo?.expectedCount || 0 }}
              </p>
              <p
                v-if="
                  (orderDetailResponse?.parcelScanInfo?.scannedTrackingNumbers
                    ?.length || 0) > 0
                "
                class="mt-1 text-xs text-black"
              >
                Numeros scannes :
              </p>
              <ul
                v-if="scannedParcels.length"
                class="mt-1 text-xs text-black space-y-1"
              >
                <li
                  v-for="item in scannedParcels"
                  :key="item.tracking"
                  class="flex flex-wrap items-center gap-x-2 gap-y-1"
                >
                  <i class="fas fa-check-circle text-green-600"></i>
                  <span>{{ getScannedParcelLabel(item) }}</span>
                  <template v-if="item.carrierTrackingNumber">
                    <span>— Numéro de suivi :</span>
                    <a
                      v-if="
                        getScannedTrackingUrl(
                          item.carrierTrackingNumber,
                          item.carrierId,
                        )
                      "
                      :href="getScannedTrackingUrl(item.carrierTrackingNumber, item.carrierId)!"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-indigo-600 hover:text-indigo-800 underline font-medium"
                    >
                      {{
                        formatScannedTracking(
                          item.carrierTrackingNumber,
                          item.carrierId,
                        )
                      }}
                    </a>
                    <strong v-else>
                      {{
                        formatScannedTracking(
                          item.carrierTrackingNumber,
                          item.carrierId,
                        )
                      }}
                    </strong>
                  </template>
                  <span v-if="item.scannedAt" class="text-black text-xs ml-1">
                    (scanné le {{ item.scannedAt }})
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Modal de confirmation -->
        <ModalComp
          :is-visible="showConfirmModal"
          :title="confirmModalTitle"
          :content="confirmModalMessage"
          cancel-text="Annuler"
          confirm-text="Confirmer"
          size="sm"
          @update:is-visible="showConfirmModal = $event"
          @confirm="executePendingAction"
        />

        <CancelOrderModal
          v-model:is-visible="showCancelModal"
          :loading="isLoading"
          @confirm="confirmCancelOrder"
        />

        <FreeShippingConfirmationModal
          v-if="selectedOrderId && orderDetailResponse"
          :is-visible="showFreeShippingConfirmationModal"
          :order-id="selectedOrderId"
          :order-group-id="Number(orderDetailResponse.id_commande_groupe || 0)"
          :order-date="orderDetailResponse.dateCommande || new Date().toISOString()"
          :invoice-id="orderDetailResponse.id_commande_name"
          :loading="isConfirmingFreeShipping"
          @update:is-visible="showFreeShippingConfirmationModal = $event"
          @confirm="handleConfirmFreeShipping"
        />

        <!-- Modal de génération d'étiquettes -->
        <LabelGenerationModal
          v-if="orderData && orderDetailResponse && selectedOrderId"
          :isVisible="showLabelGenerationModal"
          :orderId="selectedOrderId"
          :orderStatus="orderData.statut || 0"
          :idLivraison="
            orderData.id_livraison ? Number(orderData.id_livraison) : null
          "
          :carrierName="orderCarrierLibelle || undefined"
          :country-code="orderDetailResponse?.adresseLivraison?.pays?.code"
          :postal-code="orderDetailResponse?.adresseLivraison?.codePostal"
          @update:isVisible="showLabelGenerationModal = $event"
          @labels-generated="handleLabelsGenerated"
          @parcel-removed="loadOrderData(false)"
        />

        <!-- Modal dédiée à la génération d'étiquettes (après confirmation) -->
        <LabelGenerationModal
          v-if="orderData && orderDetailResponse && selectedOrderId"
          :isVisible="showRegenerateLabelModal"
          :orderId="selectedOrderId"
          :orderStatus="orderData.statut || 0"
          :idLivraison="
            orderData.id_livraison ? Number(orderData.id_livraison) : null
          "
          :carrierName="orderCarrierLibelle || undefined"
          :country-code="orderDetailResponse?.adresseLivraison?.pays?.code"
          :postal-code="orderDetailResponse?.adresseLivraison?.codePostal"
          @update:isVisible="showRegenerateLabelModal = $event"
          @labels-generated="handleLabelsRegenerated"
          @parcel-removed="loadOrderData(false)"
        />

        <ConfirmParcelDimensionsModal
          v-if="selectedOrderId"
          :is-visible="showConfirmDimensionsModal"
          :order-id="selectedOrderId"
          :initial-dimensions="existingParcelDimensions"
          :calculated-weight="orderDetailResponse?.poids"
          :country-code="orderDetailResponse?.adresseLivraison?.pays?.code"
          :postal-code="orderDetailResponse?.adresseLivraison?.codePostal"
          :id-livraison="
            orderData?.id_livraison ? Number(orderData.id_livraison) : null
          "
          :loading="isSavingParcelDimensions || isGeneratingPackageDetails"
          :require-invoice="true"
          :has-invoice="hasUploadedInvoices"
          :order-group-id="orderDetailResponse?.id_commande_groupe || 0"
          :order-date="
            orderDetailResponse?.commandeGroupe?.date_creation ||
            new Date().toISOString()
          "
          :invoice-id="orderDetailResponse?.id_commande_name || undefined"
          @update:is-visible="showConfirmDimensionsModal = $event"
          @confirm="handleConfirmDimensionsAndDownload"
          @removed="loadOrderData(false)"
          @remove-error="error"
          @invoice-uploaded="checkInvoices"
        />

        <!-- Zone Statut Commande  -->
        <div
          id="statutCommande"
          class="bg-white border border-gray-200 rounded-lg p-6 mb-6"
        >
          <h4 class="font-bold text-black mb-4">
            <span>Statut de la commande :</span>
            <span class="ml-1 font-extrabold text-blue-600">
              {{
                orderDetailResponse?.id_commande_groupe_name ||
                (orderDetailResponse?.id_commande_groupe
                  ? `#${orderDetailResponse.id_commande_groupe}`
                  : orderData?.idCommandeName || `#${selectedOrderId}`)
              }}
            </span>
          </h4>

          <!-- Actions admin: repasser en attente, annuler ou bloquer directement -->
          <div
            v-if="isAdmin && orderData"
            class="mb-4 flex flex-wrap gap-2"
          >
            <button
              v-if="orderData.statut !== 1 && orderData.statut !== 12"
              type="button"
              class="px-4 py-2 text-sm rounded-lg text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              :disabled="reverting"
              @click="askRemettreEnAttente"
            >
              <i v-if="reverting" class="fas fa-spinner fa-spin mr-1"></i>
              Repasser en attente de confirmation
            </button>
            <button
              v-if="orderData.statut !== 1 && orderData.statut !== 12"
              type="button"
              class="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              :disabled="orderData.statut === 3 || isLoading || !disableAction"
              @click="handleCancelOrder"
            >
              <i v-if="isLoading" class="fas fa-spinner fa-spin mr-1"></i>
              Annuler la commande
            </button>
            <button
              v-if="orderData.statut !== 12"
              type="button"
              class="px-4 py-2 text-sm rounded-lg text-white bg-red-700 hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
              :disabled="updatingBlocking"
              @click="demanderModificationBlocage(true)"
            >
              <i v-if="updatingBlocking" class="fas fa-spinner fa-spin mr-1"></i>
              <i v-else class="fas fa-ban mr-1"></i>
              Bloquer
            </button>
            <button
              v-else
              type="button"
              class="px-4 py-2 text-sm rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              :disabled="updatingBlocking"
              @click="demanderModificationBlocage(false)"
            >
              <i v-if="updatingBlocking" class="fas fa-spinner fa-spin mr-1"></i>
              <i v-else class="fas fa-unlock mr-1"></i>
              Débloquer
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- Étape 1: En attente de confirmation -->
            <div
              :class="[
                'text-center p-4 rounded-lg border-2 transition-all',
                getStatusClass('attente', orderData?.statut, isGroupage),
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2',
                  getStatusIconClass('attente', orderData?.statut, isGroupage),
                ]"
              >
                <i class="fas fa-clock text-white text-sm"></i>
              </div>
              <h5
                :class="[
                  'font-medium text-sm mb-1',
                  getStatusTextClass('attente', orderData?.statut, isGroupage),
                ]"
              >
                En attente de confirmation
              </h5>
              <!-- Brexit checkbox (UK uniquement, statut 1) -->
              <div
                v-if="orderData?.statut === 1 && !isGroupage && isUKOrder"
                class="mt-3 p-2 rounded border text-left"
                :class="
                  brexitChecked
                    ? 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                "
              >
                <label
                  class="flex items-start gap-2 cursor-pointer text-xs text-black"
                >
                  <input
                    type="checkbox"
                    v-model="brexitChecked"
                    class="mt-0.5 flex-shrink-0"
                  />
                  <span>
                    J'ai pris connaissance et accepte les nouvelles consignes
                    d'envois export Brexit dont la prise en charge des frais de
                    retour en cas de non-respect
                    <a
                      href="https://drive.google.com/file/d/1tzcOlS2n15NjN-uhVP92PW9wYcUGcoXs/view?usp=sharing"
                      target="_blank"
                      class="text-blue-600 hover:underline ml-1"
                      >Cliquer ici voir document</a
                    >
                  </span>
                </label>
              </div>

              <!-- Bouton Confirmer (statut 1, non groupage) -->
              <div v-if="orderData?.statut === 1 && !isGroupage" class="mt-3">
                <p
                  v-if="confirmButtonHelpMessage"
                  class="mb-2 text-sm text-amber-600 italic"
                >
                  <i class="fas fa-info-circle mr-1"></i>
                  {{ confirmButtonHelpMessage }}
                </p>
                <button
                  @click="handleConfirmOrderClick(4, 'la confirmation')"
                  :disabled="
                    !disableAction ||
                    !canConfirm ||
                    (isUKOrder && !brexitChecked)
                  "
                  class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                >
                  {{ replacementConfirmation ? 'Confirmer les remplacements' : 'Confirmer la commande' }}
                </button>
              </div>

              <!-- Bouton Confirmer (statut 1 ou 9, groupage) -->
              <div
                v-else-if="
                  (orderData?.statut === 1 || orderData?.statut === 9) &&
                  isGroupage &&
                  disableAction
                "
                class="mt-3"
              >
                <button
                  @click="handleConfirmOrderGroupage"
                  :disabled="!canConfirmGroupage"
                  class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                >
                  {{ orderData?.statut === 1 && replacementConfirmation ? 'Confirmer les remplacements' : 'Confirmer' }}
                </button>
                <p
                  v-if="confirmButtonHelpMessage"
                  class="mt-2 text-sm text-amber-600 italic"
                >
                  <i class="fas fa-info-circle mr-1"></i>
                  {{ confirmButtonHelpMessage }}
                </p>
              </div>

              <!-- Message si groupage mais facture mensuelle générée -->
              <div
                v-else-if="
                  (orderData?.statut === 1 || orderData?.statut === 9) &&
                  isGroupage &&
                  !disableAction
                "
                class="mt-3"
              >
                <p class="text-sm text-amber-600 italic">
                  <i class="fas fa-info-circle mr-1"></i>
                  Vous ne pouvez pas confirmer : la facture mensuelle a déjà été
                  générée pour ce mois.
                </p>
              </div>
            </div>

            <!-- Étape 2: Commande confirmée (normal uniquement) -->
            <div
              v-if="!isGroupage"
              :class="[
                'text-center p-4 rounded-lg border-2 transition-all',
                getStatusClass('confirm', orderData?.statut, isGroupage),
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2',
                  getStatusIconClass('confirm', orderData?.statut, isGroupage),
                ]"
              >
                <i class="fas fa-check text-white text-sm"></i>
              </div>
              <h5
                :class="[
                  'font-medium text-sm mb-1',
                  getStatusTextClass('confirm', orderData?.statut, isGroupage),
                ]"
              >
                Commande confirmée
              </h5>
              <!-- Bouton Expédier (statut 4, normal, non groupage) -->
              <div
                v-if="
                  orderData?.statut !== 1 &&
                  orderData?.statut !== 2 &&
                  orderData?.statut !== 3 &&
                  activeExpedi &&
                  disableAction
                "
                class="mt-3"
              >
                <!-- <button
                  @click="handleConfirmOrderClick(2, 'l\'expédition')"
                  :disabled="orderData?.statut !== 4"
                  class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                >
                  Expédier la commande
                </button> -->
              </div>
            </div>

            <!-- Étape 3: Attente d'enlèvement (groupage uniquement) -->
            <div
              v-if="isGroupage"
              :class="[
                'text-center p-4 rounded-lg border-2 transition-all',
                getStatusClass(
                  'attenteEnlevement',
                  orderData?.statut,
                  isGroupage,
                ),
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2',
                  getStatusIconClass(
                    'attenteEnlevement',
                    orderData?.statut,
                    isGroupage,
                  ),
                ]"
              >
                <i class="fas fa-boxes text-white text-sm"></i>
              </div>
              <h5
                :class="[
                  'font-medium text-sm mb-1',
                  getStatusTextClass(
                    'attenteEnlevement',
                    orderData?.statut,
                    isGroupage,
                  ),
                ]"
              >
                {{ pickupStepLabel }}
              </h5>
            </div>

            <!-- Étape 4: Commande expédiée -->
            <div
              :class="[
                'text-center p-4 rounded-lg border-2 transition-all',
                getStatusClass('expediee', orderData?.statut, isGroupage),
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2',
                  getStatusIconClass('expediee', orderData?.statut, isGroupage),
                ]"
              >
                <i class="fas fa-truck text-white text-sm"></i>
              </div>
              <h5
                :class="[
                  'font-medium text-sm mb-1',
                  getStatusTextClass('expediee', orderData?.statut, isGroupage),
                ]"
              >
                Commande expédiée
              </h5>
            </div>

            <!-- Étape 5: Commande annulée -->
            <div
              v-if="orderData?.statut !== 12"
              :class="[
                'text-center p-4 rounded-lg border-2 transition-all',
                getStatusClass('annulee', orderData?.statut, isGroupage),
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2',
                  getStatusIconClass('annulee', orderData?.statut, isGroupage),
                ]"
              >
                <i class="fas fa-times text-white text-sm"></i>
              </div>
              <h5
                :class="[
                  'font-medium text-sm mb-1',
                  getStatusTextClass('annulee', orderData?.statut, isGroupage),
                ]"
              >
                Commande annulée
              </h5>
              <!-- Bouton Annuler (statut 1 uniquement) -->
              <div v-if="orderData?.statut === 1 && disableAction" class="mt-3">
                <button
                  @click="handleCancelOrder"
                  class="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
            <div
              v-else
              class="text-center p-4 rounded-lg border-2 border-red-600 bg-red-50 text-red-800"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 bg-red-600"
              >
                <i class="fas fa-ban text-white text-sm"></i>
              </div>
              <h5 class="font-bold text-sm mb-1">Commande bloquée</h5>
              <p class="text-xs">Cette commande ne peut pas poursuivre son traitement.</p>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex gap-4 mt-6">
          <button
            @click="handleGoBack"
            class="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            <i class="fas fa-arrow-left"></i>
            <span>Retourner sur la liste</span>
          </button>
          <button
            @click="handleDownloadPrecommande"
            :disabled="isLoading || !orderData"
            class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-download"></i>
            <span>Télécharger la pré-commande</span>
          </button>
          <button
            v-if="canPrintJustificatifExpedition"
            @click="handleDownloadJustificatifExpedition"
            :disabled="isLoading || !orderData"
            class="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Document destiné à l'acheteur"
          >
            <i class="fas fa-print"></i>
            <span>Justificatif d'expédition</span>
          </button>
          <button
            v-if="isAdmin && !isGroupage"
            @click="handleDownloadSupplierInvoice"
            :disabled="isLoading || !orderData"
            class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-file-download"></i>
            <span>Télécharger la facture</span>
          </button>
        </div>
      </div>

      <!-- Message si aucun ID fourni -->
      <div v-else class="w-full py-8 lg:pl-6">
        <div
          v-if="!isLoading"
          class="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <div class="text-center py-12">
            <div
              class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 inline-block"
            >
              <i
                class="fas fa-exclamation-triangle text-yellow-400 text-4xl mb-4"
              ></i>
              <div class="text-yellow-800 font-medium mb-2">
                Aucune commande sélectionnée
              </div>
              <div class="text-yellow-600 text-sm">
                Veuillez sélectionner une commande depuis la liste des
                commandes.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation: repasser la commande en attente (admin) -->
    <ConfirmModal
      :show="showConfirmRemise"
      title="Repasser en attente de confirmation"
      message="Repasser cette commande en « En attente de confirmation » ?"
      confirmText="Confirmer"
      :showAttentionIcon="true"
      @confirm="confirmRemettreEnAttente"
      @cancel="cancelRemettreEnAttente"
    >
      <template #extra>
        <label
          v-if="hasExistingLabels && !isGroupage"
          class="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 cursor-pointer"
        >
          <input
            v-model="deleteLabelsOnRemise"
            type="checkbox"
            class="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <strong>Supprimer aussi les étiquettes existantes</strong>
            <span class="block mt-1 text-amber-900/80">
              Si coché, le vendeur devra les régénérer après reconfirmation.
              Sinon, les étiquettes actuelles restent disponibles.
            </span>
          </span>
        </label>
        <p
          v-else-if="hasExistingLabels && isGroupage"
          class="text-sm text-gray-600"
        >
          Les étiquettes de groupage (gérées à l’entrepôt eFashion) sont
          conservées.
        </p>
        <p v-else class="text-sm text-gray-600">
          Aucune étiquette active sur cette commande.
        </p>
      </template>
    </ConfirmModal>

    <!-- Confirmation: régénérer les étiquettes (admin, envoi normal) -->
    <ConfirmModal
      :show="showConfirmAdminRegenerateLabels"
      title="Régénérer les étiquettes"
      message="Les étiquettes actuelles seront supprimées, puis vous pourrez en générer de nouvelles. Continuer ?"
      confirmText="Continuer"
      :showAttentionIcon="true"
      @confirm="confirmAdminRegenerateNormalLabels"
      @cancel="showConfirmAdminRegenerateLabels = false"
    />
    <OrderBlockingScopeModal
      v-if="selectedOrderId"
      :is-visible="blocageModalVisible"
      :bloque="blocageDemande"
      :id-commande="selectedOrderId"
      :loading="updatingBlocking"
      @update:is-visible="blocageModalVisible = $event"
      @select="modifierBlocage"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  onMounted,
  onUnmounted,
  provide,
  computed,
  nextTick,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import type {
  CarrierType,
  OrderDetailData,
  OrderDetailResponse,
  ParcelDimension,
} from "@/types";
import {
  getOrderById,
  updateOrderStatus,
  OrderConfirmationCancelledError,
  getOrderValidations,
  downloadPrecommande,
  downloadJustificatifExpedition,
  downloadSupplierInvoice,
  getUploadedInvoices,
  getOrderLabels,
  generatePackageDetailsPDF,
  saveParcelDimensions,
  type FreeShippingTrackingInput,
} from "@/api/orders";
import { mapCarrierIdToType } from "@/utils/carrierMapper";
import { mapToOrderSummary } from "@/utils/orderMapper";
import { getLabelModule, isGpxCarrier } from "@/utils/labelModuleSelector";
import { getCarrierMessage } from "@/utils/carrierMessage";
import { getTrackingUrl } from "@/utils/trackingUrl";
import ClientInfo from "@/components/order/ClientInfo.vue";
import AddressDetails from "@/components/order/AddressDetails.vue";
import OrderAdminZone from "@/components/order/OrderAdminZone.vue";
import OrderAdminAddresses from "@/components/order/OrderAdminAddresses.vue";
import OrderDetailsSection from "@/components/order/OrderDetailsSection.vue";
import LabelColissimo from "@/components/order/labels/LabelColissimo.vue";
import LabelDPD from "@/components/order/labels/LabelDPD.vue";
import LabelUPS from "@/components/order/labels/LabelUPS.vue";
import LabelEasy from "@/components/order/labels/LabelEasy.vue";
import LabelFreeShipping from "@/components/order/labels/LabelFreeShipping.vue";
import ModalComp from "@/components/ui/ModalComp.vue";
import LabelGenerationModal from "@/components/modals/LabelGenerationModal.vue";
import ConfirmParcelDimensionsModal from "@/components/modals/ConfirmParcelDimensionsModal.vue";
import CancelOrderModal from "@/components/modals/CancelOrderModal.vue";
import FreeShippingConfirmationModal from "@/components/modals/FreeShippingConfirmationModal.vue";
import { ConfirmModal } from "@/components";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import { getApiBaseUrl } from "@/config/api";
import OrderBlockingScopeModal, {
  type OrderBlockingScope,
} from "@/components/adminEFP/orders/OrderBlockingScopeModal.vue";
import { adminEfpSoftDeleteAllEtiquettes } from "@/api/adminEfp";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// Mode admin (impersonation depuis le back-office EFP): permet de repasser une
// commande déjà confirmée en « En attente de confirmation » directement depuis
// la page de détail (même action que la modal AcheteurValidationModal).
const isAdmin = computed(() => authStore.isImpersonating);
const reverting = ref(false);
const updatingBlocking = ref(false);
const blocageModalVisible = ref(false);
const blocageDemande = ref(false);
const showConfirmRemise = ref(false);
const deleteLabelsOnRemise = ref(false);
const showCancelModal = ref(false);
const showFreeShippingConfirmationModal = ref(false);
const isConfirmingFreeShipping = ref(false);
const pendingFreeShippingStatus = ref(4);

const askRemettreEnAttente = () => {
  if (!selectedOrderId.value) return;
  // Décoché par défaut (sécurité) — l’admin doit cocher explicitement
  deleteLabelsOnRemise.value = false;
  showConfirmRemise.value = true;
};

const cancelRemettreEnAttente = () => {
  showConfirmRemise.value = false;
};

const confirmRemettreEnAttente = async () => {
  showConfirmRemise.value = false;
  if (!selectedOrderId.value) return;
  reverting.value = true;
  try {
    const shouldDeleteLabels =
      !isGroupage.value && Boolean(deleteLabelsOnRemise.value);
    const res = await fetch(
      `${getApiBaseUrl()}/api/admin-efp/commandes/${
        selectedOrderId.value
      }/remettre-en-attente`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deleteLabels: shouldDeleteLabels,
        }),
      },
    );
    const json = await res.json();
    if (!res.ok || !json.ok) {
      throw new Error(json?.error || `Erreur ${res.status}`);
    }
    const deleted = Number(json.labels_deleted || 0);
    success(
      deleted > 0
        ? `Commande remise en attente (${deleted} étiquette${deleted > 1 ? "s" : ""} supprimée${deleted > 1 ? "s" : ""})`
        : "Commande remise en attente de confirmation",
    );
    await loadOrderData(false);
  } catch (e) {
    error(
      e instanceof Error ? e.message : "Erreur lors de la remise en attente",
    );
  } finally {
    reverting.value = false;
  }
};

const demanderModificationBlocage = (bloque: boolean) => {
  blocageDemande.value = bloque;
  blocageModalVisible.value = true;
};

const modifierBlocage = async (portee: OrderBlockingScope) => {
  if (!selectedOrderId.value || updatingBlocking.value) return;
  const bloque = blocageDemande.value;
  updatingBlocking.value = true;
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin-efp/commandes/${selectedOrderId.value}/blocage`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bloque, portee }),
      },
    );
    const result = await response.json();
    if (!response.ok || !result?.ok) {
      throw new Error(result?.message || result?.error || `Erreur ${response.status}`);
    }
    if (orderData.value && Number(result.statut) >= 0) {
      orderData.value.statut = Number(result.statut);
    }
    if (orderDetailResponse.value && Number(result.statut) >= 0) {
      orderDetailResponse.value.statut = Number(result.statut);
    }
    blocageModalVisible.value = false;
    success(
      portee === "groupe"
        ? bloque
          ? "Commandes du groupe bloquées"
          : "Commandes du groupe débloquées"
        : bloque
          ? "Commande bloquée"
          : "Commande débloquée",
    );
    await loadOrderData(false);
  } catch (exception) {
    error(
      exception instanceof Error
        ? exception.message
        : "Erreur lors de la modification du blocage",
    );
  } finally {
    updatingBlocking.value = false;
  }
};
// État
const selectedOrderId = ref<number | null>(null);
const orderData = ref<OrderDetailData | null>(null);
const orderCarrier = ref<CarrierType | null>(null);
const orderCarrierLibelle = ref<string | null>(null); // Libellé exact depuis la DB
const showOrderSummary = ref(false);
const isGroupage = ref(false);
// Vue simplifiée lorsqu'on arrive depuis la gestion des groupages
const isFromGroupage = computed(() => route.query.from === "groupage");
const canConfirm = ref(false);
const replacementConfirmation = ref(false);
watch(selectedOrderId, () => { replacementConfirmation.value = false; }, { flush: 'sync' });
const brexitChecked = ref(false);
const orderDetailResponse = ref<OrderDetailResponse | null>(null);

type ScannedParcel = NonNullable<
  OrderDetailResponse["parcelScanInfo"]
>["scannedTrackingNumbers"][number];

const scannedParcels = computed<ScannedParcel[]>(
  () => orderDetailResponse.value?.parcelScanInfo?.scannedTrackingNumbers ?? [],
);

const getScannedParcelLabel = (item: ScannedParcel): string => {
  if (item.parcelNumber != null) return `Colis n°${item.parcelNumber}`;
  return /^\d+$/.test(item.tracking)
    ? `Colis n°${item.tracking}`
    : `Numéro scanné : ${item.tracking}`;
};

const isDpdDelivery = (carrierId?: number | null): boolean =>
  [10, 13, 2743].includes(Number(carrierId));

const formatScannedTracking = (
  tracking: string,
  carrierId?: number | null,
): string =>
  isDpdDelivery(carrierId) && !tracking.startsWith("092")
    ? `092${tracking}`
    : tracking;

const getScannedTrackingUrl = (
  tracking: string,
  carrierId?: number | null,
): string | null =>
  getTrackingUrl(
    isDpdDelivery(carrierId) && tracking.startsWith("092")
      ? tracking.slice(3)
      : tracking,
    carrierId,
  );

const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const disableAction = ref(true);
const activeExpedi = ref(true);
const canConfirmGroupage = ref(true);
const canReadyForPickup = ref(true);
// Validations détaillées pour les messages d'aide
// Vérifier si au moins une facture existe
const hasUploadedInvoices = ref(false);
const invoicesCount = ref(0);

// États pour les collapses
const isOrderDetailsCollapsed = ref(true);
const isClientInfoCollapsed = ref(true);
const isAddressDetailsCollapsed = ref(true);

const GROUPAGE_WITHOUT_PACKAGE_DETAILS_IDS: string[] = [];

const groupageReadiness = computed(() => {
  return orderDetailResponse.value?.groupageReadiness || null;
});

const shouldShowGroupageReadiness = computed(() => {
  if (!isGroupage.value) return false;
  if (!orderData.value?.id_livraison) return false;
  const id = String(orderData.value.id_livraison);
  if (!GROUPAGE_WITHOUT_PACKAGE_DETAILS_IDS.includes(id)) return false;
  return !!groupageReadiness.value;
});

const isPickupEffectue = computed(() => {
  return (
    isGroupage.value &&
    [8, 14].includes(Number(orderData.value?.statut))
  );
});

const canAdminGenerateUsFinalLabels = computed(
  () =>
    isAdmin.value &&
    isGroupage.value &&
    [8, 14].includes(Number(orderData.value?.statut)) &&
    orderDetailResponse.value?.requiresUsCustomsCompliance === true,
);

const pickupStepLabel = computed(() => {
  if (Number(orderData.value?.statut) === 14) return "Reçu au dépôt";
  return isPickupEffectue.value ? "Pickup effectué" : "Attente d'enlèvement";
});

// Computed pour déterminer si on doit afficher la section d'impression des détails de colis
// Afficher pour: groupages OU transporteurs propres du client (non gérés par eFashion)
const shouldShowPackageDetails = computed(() => {
  if (!orderData.value) return false;

  // Vérifier que la commande est confirmée (dont 14 = reçue au dépôt).
  const statut = orderData.value.statut || 0;
  const isCommandeConfirmee = [2, 4, 7, 8, 9, 14].includes(statut);
  if (!isCommandeConfirmee) return false;
  if (isGroupage.value && [2, 8, 14].includes(Number(statut))) {
    return false;
  }
  if (isFreeShippingOrder.value) return false;
  if (isOwnCarrierDirectOrder.value) return true;

  const idLivraison = orderData.value.id_livraison;
  if (!idLivraison || isNaN(idLivraison)) return true;

  return (
    isGroupage.value &&
    !GROUPAGE_WITHOUT_PACKAGE_DETAILS_IDS.includes(String(idLivraison))
  );
});

// Pour les groupages, afficher la zone étiquettes pour les transporteurs hors liste d'exclusion
const shouldShowLabelZone = computed(() => {
  if (canAdminGenerateUsFinalLabels.value) return true;
  if (!orderData.value?.statut) return false;
  const statut = Number(orderData.value.statut);
  if (![2, 4, 7, 8, 9, 14].includes(statut)) return false;
  const id = Number(orderData.value.id_livraison);

  return (
    !isGroupage.value ||
    (id && GROUPAGE_WITHOUT_PACKAGE_DETAILS_IDS.includes(String(id)))
  );
});

/** Document acheteur : disponible dès qu'il y a des étiquettes ou une expédition. */
const canPrintJustificatifExpedition = computed(() => {
  if (!orderData.value) return false;
  const statut = Number(orderData.value.statut);
  if ([1, 3].includes(statut) || statut <= 0) return false;
  if (hasExistingLabels.value) return true;
  if (orderDetailResponse.value?.dateExpedition) return true;
  return [2, 7, 8, 14].includes(statut);
});

const canShowLabelGenerateCta = computed(() => {
  if (!orderData.value || isGroupage.value) return false;
  if (isClientTransporterOrder.value) return false;
  const statut = Number(orderData.value.statut);
  if ([1, 3, 9].includes(statut) || statut <= 0) return false;
  const idLivraison = Number(orderData.value.id_livraison);
  if (!idLivraison || idLivraison === 2 || idLivraison === 3) return false;
  if (!getLabelModule(idLivraison)) return false;
  return !hasExistingLabels.value;
});

const canShowAdminRegenerateLabelsCta = computed(() => {
  if (!isAdmin.value) return false;
  if (!orderData.value || isGroupage.value) return false;
  if (isClientTransporterOrder.value) return false;
  if (!hasExistingLabels.value) return false;
  const statut = Number(orderData.value.statut);
  if (![2, 4].includes(statut)) return false;
  const idLivraison = Number(orderData.value.id_livraison);
  if (!idLivraison || idLivraison === 2 || idLivraison === 3) return false;
  return Boolean(getLabelModule(idLivraison));
});

// État pour la génération
const isGeneratingPackageDetails = ref(false);
const showConfirmDimensionsModal = ref(false);
const isSavingParcelDimensions = ref(false);

const hasExistingLabels = ref(false);
const isCheckingLabels = ref(false);
const isGeneratingAdminUsLabels = ref(false);
const isAdminRegeneratingLabels = ref(false);
const showConfirmAdminRegenerateLabels = ref(false);

const normalizeParcelDimension = (
  dim: Record<string, unknown> | unknown[],
): ParcelDimension => {
  const values = Array.isArray(dim)
    ? dim
    : [dim.longueur, dim.largeur, dim.hauteur, dim.poids];
  const numberOrNull = (value: unknown) => {
    const parsed = Number(value);
    return value !== "" && value != null && Number.isFinite(parsed)
      ? parsed
      : null;
  };
  return {
    longueur: numberOrNull(values[0]),
    largeur: numberOrNull(values[1]),
    hauteur: numberOrNull(values[2]),
    poids: numberOrNull(values[3]),
  };
};

const existingParcelDimensions = computed<ParcelDimension[]>(() => {
  const raw: unknown = (orderDetailResponse.value as { data?: unknown } | null)
    ?.data;
  let parsed: unknown = {};
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  } else if (raw) {
    parsed = raw;
  }
  const infocolis = (
    parsed as {
      infocolis?: { dimensioncolis?: unknown; nbcolis?: unknown };
    } | null
  )?.infocolis;
  const list = Array.isArray(infocolis?.dimensioncolis)
    ? infocolis.dimensioncolis
    : [];
  const nbColis =
    Number(orderDetailResponse.value?.nb_colis) ||
    Number(infocolis?.nbcolis) ||
    list.length ||
    1;
  return Array.from({ length: nbColis }, (_, index) =>
    normalizeParcelDimension(
      (list[index] || {}) as Record<string, unknown> | unknown[],
    ),
  );
});

// Message d'information transporteur (normal vs groupage)
const carrierMessage = computed(() => {
  if (!orderData.value?.id_livraison) {
    return null;
  }

  const idLivraison = Number(orderData.value.id_livraison);

  // Pas de message pour retrait en magasin et livraison offerte
  if (idLivraison === 2 || idLivraison === 3) {
    return null;
  }

  const dateExpedition = orderDetailResponse.value?.dateExpedition || undefined;

  return getCarrierMessage(
    isGroupage.value,
    dateExpedition,
    orderCarrierLibelle.value || undefined,
  );
});

const isClientTransporterOrder = computed(() => {
  const livraison = orderDetailResponse.value?.livraison;
  return livraison?.id_acheteur != null;
});

const isOwnCarrierDirectOrder = computed(() =>
  isClientTransporterOrder.value &&
  orderDetailResponse.value?.commandeGroupe?.groupage != null &&
  Number(orderDetailResponse.value.commandeGroupe.groupage) === 0 &&
  orderData.value?.id_livraison != null &&
  ![2, 3].includes(Number(orderData.value.id_livraison)),
);

const shouldShowParcelScanInfo = computed(() => {
  if (!orderDetailResponse.value?.parcelScanInfo) return false;
  const statut = Number(orderData.value?.statut || 0);
  // "Nouveau" dans l'app: 1 et 9
  return ![1, 9].includes(statut);
});

// Gérer la génération du PDF
const downloadPackageDetailsPDF = async (colis?: ParcelDimension[]) => {
  if (!selectedOrderId.value) return;

  // Ouvrir tout de suite un onglet pour eviter le blocage popup
  const openedWindow = window.open("", "_blank");

  isGeneratingPackageDetails.value = true;
  try {
    const result = await generatePackageDetailsPDF(
      selectedOrderId.value,
      colis,
    );

    // Rediriger l'onglet ouvert vers le PDF genere
    if (result.generateUrl) {
      if (openedWindow) {
        openedWindow.location.href = result.generateUrl;
      } else {
        window.open(result.generateUrl, "_blank");
      }
      success(
        "Génération du PDF lancée. Le fichier sera disponible après génération.",
      );
    } else {
      throw new Error("URL storage du PDF absente");
    }
  } catch (err) {
    if (openedWindow && !openedWindow.closed) {
      openedWindow.close();
    }
    error(
      err instanceof Error
        ? err.message
        : "Erreur lors de la génération du PDF",
    );
  } finally {
    isGeneratingPackageDetails.value = false;
  }
};

const handleGeneratePackageDetails = async () => {
  if (!selectedOrderId.value) return;

  // Ouvrir la modale même sans facture : l'upload est proposé dans la modale.
  await checkInvoices();
  showConfirmDimensionsModal.value = true;
};

const handleConfirmDimensionsAndDownload = async (
  dimensions: ParcelDimension[],
) => {
  if (!selectedOrderId.value) return;

  await checkInvoices();
  if (!hasUploadedInvoices.value) {
    return;
  }

  isSavingParcelDimensions.value = true;
  try {
    await saveParcelDimensions(selectedOrderId.value, dimensions);
    showConfirmDimensionsModal.value = false;
    // Passer les dimensions au générateur pour éviter une relecture BDD obsolète
    await downloadPackageDetailsPDF(dimensions);
    await loadOrderData(false);
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Erreur lors de l'enregistrement des dimensions",
    );
  } finally {
    isSavingParcelDimensions.value = false;
  }
};

const refreshLabelsState = async () => {
  if (!selectedOrderId.value) {
    hasExistingLabels.value = false;
    return;
  }

  isCheckingLabels.value = true;
  try {
    const labels = await getOrderLabels(selectedOrderId.value);
    hasExistingLabels.value = labels.length > 0;
  } catch {
    // En cas d'erreur, ne pas bloquer l'UI et considérer "pas d'étiquette"
    hasExistingLabels.value = false;
  } finally {
    isCheckingLabels.value = false;
  }
};

const handleRegenerateLabels = () => {
  showRegenerateLabelModal.value = true;
};

const askAdminRegenerateNormalLabels = () => {
  if (!selectedOrderId.value || !canShowAdminRegenerateLabelsCta.value) return;
  showConfirmAdminRegenerateLabels.value = true;
};

const confirmAdminRegenerateNormalLabels = async () => {
  showConfirmAdminRegenerateLabels.value = false;
  if (!selectedOrderId.value) return;

  isAdminRegeneratingLabels.value = true;
  try {
    const result = await adminEfpSoftDeleteAllEtiquettes(selectedOrderId.value);
    if (!result.ok) {
      throw new Error(result.error || "Impossible de supprimer les étiquettes");
    }
    await refreshLabelsState();
    success(
      result.deleted && result.deleted > 0
        ? `${result.deleted} étiquette${result.deleted > 1 ? "s" : ""} supprimée${result.deleted > 1 ? "s" : ""} — vous pouvez en générer de nouvelles`
        : "Aucune étiquette à supprimer — vous pouvez générer",
    );
    showRegenerateLabelModal.value = true;
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de préparer la régénération des étiquettes",
    );
  } finally {
    isAdminRegeneratingLabels.value = false;
  }
};

const generateAdminUsFinalLabels = async () => {
  const idCommande = selectedOrderId.value;
  const idCommandeGroupe = Number(
    orderDetailResponse.value?.id_commande_groupe,
  );
  if (!idCommande || !Number.isInteger(idCommandeGroupe)) return;

  isGeneratingAdminUsLabels.value = true;
  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/admin-efp/groupages/genetiquette-easy`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idCommandeGroupe, idCommande }),
      },
    );
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(
        result.message || "Impossible de générer les étiquettes USA",
      );
    }
    success("Étiquettes USA générées pour cette commande uniquement");
    await loadOrderData(false);
    await refreshLabelsState();
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de générer les étiquettes USA",
    );
  } finally {
    isGeneratingAdminUsLabels.value = false;
  }
};

// Computed pour créer un objet de props pour LabelEsendeo

// Vérifier les factures existantes (utilisé pour les validations internes)
const checkInvoices = async () => {
  if (!selectedOrderId.value) return;
  try {
    const invoices = await getUploadedInvoices(selectedOrderId.value);
    invoicesCount.value = invoices.length;
    hasUploadedInvoices.value = invoices.length > 0;
  } catch (error) {
    invoicesCount.value = 0;
    hasUploadedInvoices.value = false;
  }
};

// Modal de confirmation
const showConfirmModal = ref(false);
const confirmModalTitle = ref("");
const confirmModalMessage = ref("");
const pendingAction = ref<(() => Promise<void>) | null>(null);
const showLabelGenerationModal = ref(false);
const showRegenerateLabelModal = ref(false);
const pendingGroupageStatus = ref<number | null>(null);

// Notifications
const { success, error } = useNotification();

// Déterminer le module d'étiquette à utiliser
const labelModule = computed(() => {
  if (orderData.value && orderData.value.id_livraison == null) {
    return "freeShipping";
  }
  if (!orderData.value?.id_livraison) return null;
  return getLabelModule(orderData.value.id_livraison);
});

const isFreeShippingOrder = computed(
  () => Boolean(orderData.value) && orderData.value?.id_livraison == null,
);

// Détecter si la commande est à destination du Royaume-Uni (code GB ou id_pays = 24)
const isUKOrder = computed(() => {
  const pays = orderDetailResponse.value?.adresseLivraison?.pays;
  return pays?.code === "GB" || pays?.id_pays === 24;
});

// Messages d'aide pour expliquer pourquoi le bouton est désactivé
const confirmButtonHelpMessage = computed(() => {
  // Commande normale (non groupage)
  if (orderData.value?.statut === 1 && !isGroupage.value) {
    if (!disableAction.value) {
      return "Vous ne pouvez pas confirmer : la facture mensuelle a déjà été générée pour ce mois.";
    }
    if (isUKOrder.value && !brexitChecked.value) {
      return "Veuillez cocher la case Brexit pour confirmer la commande.";
    }
    return null;
  }

  // Commande groupage
  // Plus de blocage lié à la facture (gérée dans la modal)
  return null;
});

// Fournir les données aux composants enfants via provide/inject
// Créer un objet réactif avec la valeur de orderDetailResponse
provide(
  "orderDetailData",
  computed(() => ({ value: orderDetailResponse.value })),
);

// Méthodes
const handleGoBack = () => {
  // Si on vient de la gestion des groupages, revenir au groupage d'origine
  if (route.query.from === "groupage") {
    router.push({
      name: "adminEFP/groupage-detail",
      params: { id: route.query.groupeId as string },
    });
    return;
  }
  // Conserver les query parameters lors du retour vers la liste
  router.push({
    name: "myorders",
    query: route.query, // Conserver tous les filtres dans l'URL
  });
};

const handleDownloadPrecommande = async () => {
  if (!selectedOrderId.value) return;

  try {
    isLoading.value = true;
    await downloadPrecommande(selectedOrderId.value);
    success("Pré-commande téléchargée avec succès");
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : "Erreur lors du téléchargement de la pré-commande";
    error(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

const handleDownloadJustificatifExpedition = async () => {
  if (!selectedOrderId.value) return;

  try {
    isLoading.value = true;
    await downloadJustificatifExpedition(selectedOrderId.value);
    success("Justificatif d'expédition généré avec succès");
  } catch (err: unknown) {
    error(
      err instanceof Error
        ? err.message
        : "Erreur lors de la génération du justificatif d'expédition",
    );
  } finally {
    isLoading.value = false;
  }
};

const handleDownloadSupplierInvoice = async () => {
  if (!selectedOrderId.value) return;

  try {
    isLoading.value = true;
    await downloadSupplierInvoice(selectedOrderId.value);
    success("Facture téléchargée avec succès");
  } catch (err: unknown) {
    error(
      err instanceof Error
        ? err.message
        : "Erreur lors du téléchargement de la facture",
    );
  } finally {
    isLoading.value = false;
  }
};

// Fonctions pour gérer les classes CSS avec le design original
const getStatusClass = (
  step: string,
  statut: number | null | undefined,
  groupage: boolean,
): string => {
  if (!statut) return "border-gray-200 bg-gray-50";

  switch (step) {
    case "attente":
      if (statut === 1 && !groupage) return "border-yellow-300 bg-yellow-50";
      if ((statut === 1 || statut === 9) && groupage) {
        const statutNum = Number(statut);
        const isExpediee =
          (statutNum >= 7 && statutNum !== 9) || statutNum === 2;
        return isExpediee
          ? "border-green-500 bg-green-50"
          : "border-yellow-300 bg-yellow-50";
      }
      return "border-gray-200 bg-gray-50";

    case "confirm":
      if (statut === 1 || statut === 3) return "border-gray-200 bg-gray-50";
      return "border-blue-300 bg-blue-50";

    case "attenteEnlevement":
      if ([7, 8, 14].includes(statut)) {
        const statutNum = Number(statut);
        const isPickupEffectue = [8, 14].includes(statutNum);
        return isPickupEffectue
          ? "border-lime-400 bg-lime-50"
          : "border-blue-300 bg-blue-50";
      }
      return "border-gray-200 bg-gray-50";

    case "expediee":
      if (statut === 2) return "border-green-500 bg-green-50";
      return "border-gray-200 bg-gray-50";

    case "annulee":
      if (statut === 3) return "border-red-300 bg-red-50";
      return "border-gray-200 bg-gray-50";

    default:
      return "border-gray-200 bg-gray-50";
  }
};

const getStatusIconClass = (
  step: string,
  statut: number | null | undefined,
  groupage: boolean,
): string => {
  if (!statut) return "bg-gray-400";

  switch (step) {
    case "attente":
      if (statut === 1 && !groupage) return "bg-yellow-400";
      if ((statut === 1 || statut === 9) && groupage) {
        const statutNum = Number(statut);
        const isExpediee =
          (statutNum >= 7 && statutNum !== 9) || statutNum === 2;
        return isExpediee ? "bg-green-500" : "bg-yellow-400";
      }
      return "bg-gray-400";

    case "confirm":
      if (statut === 1 || statut === 3) return "bg-gray-400";
      return "bg-blue-500";

    case "attenteEnlevement":
      if ([7, 8, 14].includes(statut)) {
        const statutNum = Number(statut);
        const isPickupEffectue = [8, 14].includes(statutNum);
        return isPickupEffectue ? "bg-lime-400" : "bg-blue-500";
      }
      return "bg-gray-400";

    case "expediee":
      if (statut === 2) return "bg-green-500";
      return "bg-gray-400";

    case "annulee":
      if (statut === 3) return "bg-red-500";
      return "bg-gray-400";

    default:
      return "bg-gray-400";
  }
};

const getStatusTextClass = (
  step: string,
  statut: number | null | undefined,
  groupage: boolean,
): string => {
  if (!statut) return "text-black";

  switch (step) {
    case "attente":
      if (statut === 1 && !groupage) return "text-black";
      if ((statut === 1 || statut === 9) && groupage) {
        const statutNum = Number(statut);
        const isExpediee =
          (statutNum >= 7 && statutNum !== 9) || statutNum === 2;
        return isExpediee ? "text-green-700" : "text-black";
      }
      return "text-black";

    case "confirm":
      if (statut === 1 || statut === 3) return "text-black";
      return "text-black";

    case "attenteEnlevement":
      if ([7, 8, 14].includes(statut)) {
        const statutNum = Number(statut);
        const isPickupEffectue = [8, 14].includes(statutNum);
        return isPickupEffectue ? "text-lime-700" : "text-gray-900";
      }
      return "text-black";

    case "expediee":
      if (statut === 2) return "text-green-700";
      return "text-black";

    case "annulee":
      if (statut === 3) return "text-red-700";
      return "text-black";

    default:
      return "text-black";
  }
};

// Exécuter l'action en attente après confirmation
const executePendingAction = async () => {
  if (pendingAction.value) {
    await pendingAction.value();
    pendingAction.value = null;
  }
};

// Actions pour modifier le statut
const handleConfirmOrder = async (
  newStatus: number,
  reasonCode?: string,
  reasonDetail?: string,
  freeShippingTracking?: FreeShippingTrackingInput,
  showPageLoading = true,
): Promise<boolean> => {
  if (!selectedOrderId.value || !disableAction.value) return false;

  try {
    if (showPageLoading) isLoading.value = true;
    const updatedOrder = await updateOrderStatus(
      selectedOrderId.value,
      newStatus,
      reasonCode,
      reasonDetail,
      freeShippingTracking,
    );

    // Recharger les données
    await loadOrderData();

    // Toast de succès
    const statusMessages: Record<number, string> = {
      2: "Commande expédiée avec succès",
      3: "Commande annulée",
      4: "Commande confirmée avec succès",
      7: "Commande marquée comme prête pour pickup",
      8: "Commande confirmée avec succès",
    };
    const closedEmptyOrder = [4, 7, 8].includes(newStatus) && updatedOrder?.statut === 3;
    success(closedEmptyOrder ? "Aucun article à expédier : commande clôturée et annulée." : statusMessages[newStatus] || "Statut modifié avec succès");
    return true;
  } catch (err) {
    if (err instanceof OrderConfirmationCancelledError) return false;
    error(
      err instanceof Error
        ? err.message
        : "Erreur lors de la modification du statut de la commande",
    );
    return false;
  } finally {
    if (showPageLoading) isLoading.value = false;
  }
};

const handleConfirmFreeShipping = async (
  tracking: FreeShippingTrackingInput,
) => {
  isConfirmingFreeShipping.value = true;
  try {
    const confirmed = await handleConfirmOrder(
      pendingFreeShippingStatus.value,
      undefined,
      undefined,
      tracking,
      false,
    );
    if (confirmed) showFreeShippingConfirmationModal.value = false;
  } finally {
    isConfirmingFreeShipping.value = false;
  }
};

const handleConfirmOrderClick = (newStatus: number, actionName: string) => {
  if (!selectedOrderId.value || !disableAction.value) return;

  // Groupage US : notice conformité avant confirmation (sans dimensions)
  if (
    (newStatus === 7 || newStatus === 8) &&
    isGroupage.value &&
    orderDetailResponse.value?.requiresUsCustomsCompliance === true &&
    orderDetailResponse.value?.usCustomsNoticeAccepted !== true
  ) {
    showLabelGenerationModal.value = true;
    return;
  }

  if ([4, 7, 8].includes(newStatus) && isFreeShippingOrder.value) {
    pendingFreeShippingStatus.value = newStatus;
    showFreeShippingConfirmationModal.value = true;
    return;
  }

  // Transporteur acheteur : dimensions + facture dans la modal, statut 7
  if (newStatus === 4 && isClientTransporterOrder.value) {
    showLabelGenerationModal.value = true;
    return;
  }

  // Messages de confirmation selon l'action
  if ([4, 7, 8].includes(newStatus)) {
    void handleConfirmOrder(newStatus);
    return;
  }
  const confirmMessages: Record<number, string> = {
    2: "Êtes-vous sûr de vouloir expédier cette commande ?",
    3: "Êtes-vous sûr de vouloir annuler cette commande ?",
  };

  confirmModalTitle.value = `Confirmer ${actionName}`;
  confirmModalMessage.value =
    confirmMessages[newStatus] ||
    "Êtes-vous sûr de vouloir effectuer cette action ?";
  pendingAction.value = () => handleConfirmOrder(newStatus);
  showConfirmModal.value = true;
};

// Après labels / notice US groupage : confirmer le statut si besoin
const handleLabelsGenerated = async () => {
  if (isGroupage.value && pendingGroupageStatus.value !== null) {
    await handleConfirmOrder(pendingGroupageStatus.value);
    pendingGroupageStatus.value = null;
  }
  await loadOrderData(false);
  await refreshLabelsState();
};

// Variante "régénération" : même formulaire, mais sans logique de confirmation de statut
const handleLabelsRegenerated = () => {
  loadOrderData(false);
  refreshLabelsState();
  success("Étiquettes régénérées avec succès");
};

const handleConfirmOrderGroupage = () => {
  if (!selectedOrderId.value || !disableAction.value) return;

  // Pour le groupage, si statut = 9, passer à 8, sinon passer à 7
  const newStatus = orderData.value?.statut === 9 ? 8 : 7;

  // Stocker le statut cible (utile si notice US via LabelGenerationModal)
  pendingGroupageStatus.value = newStatus;

  handleConfirmOrderClick(newStatus, "la confirmation");
};

const handleCancelOrder = () => {
  if (!selectedOrderId.value || !disableAction.value) return;
  showCancelModal.value = true;
};

const confirmCancelOrder = async (
  reasonCode: string,
  reasonDetail?: string,
) => {
  await handleConfirmOrder(3, reasonCode, reasonDetail);
  showCancelModal.value = false;
};

// Fonction pour charger les données de la commande
const loadOrderData = async (showLoading = true) => {
  if (!selectedOrderId.value) return false;

  if (showLoading) {
    isLoading.value = true;
  }
  errorMessage.value = null;

  // Vérifier les factures pour les validations internes
  try {
    await checkInvoices();
  } catch (error) {
    // Erreur silencieuse
  }

  try {
    const data: OrderDetailResponse | null = await getOrderById(
      selectedOrderId.value,
    );

    // Vérifier si la commande existe
    if (!data) {
      errorMessage.value = "Commande introuvable";
      isLoading.value = false;
      return false;
    }

    // Stocker la réponse complète pour les composants enfants
    // Mapper Commentaires (C majuscule) vers commentaires (c minuscule) si nécessaire
    // car GraphQL peut retourner le nom exact tel qu'il est dans l'entité
    if ((data as any).Commentaires !== undefined) {
      // Utiliser Commentaires (C majuscule) si commentaires (c minuscule) n'existe pas ou est null
      if (data.commentaires === undefined || data.commentaires === null) {
        data.commentaires = (data as any).Commentaires;
      }
    }
    orderDetailResponse.value = data;

    // Mapper les données de l'API vers orderData
    const summary = mapToOrderSummary(data);
    orderData.value = {
      totalHT: summary.totalHT,
      tva: summary.tva,
      totalTTC: summary.totalTTC,
      quantityTotal: summary.quantityTotal,
      weight: summary.weight,
      packages: summary.packages,
      id_livraison: data.id_livraison ?? data.livraison?.id_livraison ?? null,
      statut: data.statut,
      groupage: data.groupage,
      carrierMessage: data.livraison?.commentaires || undefined,
      idCommandeName: data.id_commande_name || undefined,
    };

    // Mapper le transporteur (pour le badge visuel uniquement) - seulement si livraison existe
    if (data.livraison) {
      orderCarrier.value = mapCarrierIdToType(data.livraison.id_livraison);
      // Stocker le libellé exact depuis la DB (comme dans la liste)
      orderCarrierLibelle.value = data.livraison.libelle;
    } else {
      orderCarrier.value = null;
      orderCarrierLibelle.value = null;
    }

    // Afficher le récapitulatif si statut = 4
    showOrderSummary.value = data.statut === 4;

    // Un GPX unique en UE peut encore générer son étiquette directement.
    // Une destination hors UE reste en groupage, même avec un seul colis GPX.
    const isSingleGpxOrder =
      isGpxCarrier(data.id_livraison) &&
      Number(data.groupageReadiness?.countTotal) === 1;
    const explicitGroupage =
      data.commandeGroupe?.groupage === 1 || data.groupage === true;
    const singleGpxShipsDirect =
      isSingleGpxOrder && data.outsideEuShippingZone !== true;
    isGroupage.value = explicitGroupage && !singleGpxShipsDirect;

    // Peut confirmer si statut = 1 (en attente)
    canConfirm.value = data.statut === 1;

    // Charger les validations
    await loadOrderValidations();
    await refreshLabelsState();
    return true;
  } catch (error) {
    if (error instanceof Error) {
      errorMessage.value = error.message.includes("introuvable")
        ? "Commande introuvable"
        : "Erreur lors du chargement de la commande";
    } else {
      errorMessage.value = "Erreur lors du chargement de la commande";
    }
    return false;
  } finally {
    if (showLoading) {
      isLoading.value = false;
    }
  }
};

// Fonction de rechargement silencieux (sans afficher le spinner)
const reloadOrderDataSilent = async () => {
  return loadOrderData(false);
};

// Fournir la fonction de rechargement pour permettre la mise à jour dynamique
// (après la déclaration de loadOrderData)
provide("reloadOrderData", reloadOrderDataSilent);

// Gérer l'upload d'une facture
const handleInvoiceUploaded = async (
  action: "upload" | "delete" = "upload",
) => {
  // Vérifier immédiatement les factures pour mettre à jour hasUploadedInvoices
  await checkInvoices();
  // Recharger les validations pour mettre à jour les autres flags
  await loadOrderValidations();

  // Ne pas ouvrir automatiquement la modal lors d'une suppression de facture
  if (action !== "upload") return;

  // Si la commande est en attente après upload, ouvrir directement la modal de confirmation
  const currentStatus = orderData.value?.statut;
  if (!disableAction.value) return;

  // Commande normale en attente
  if (
    currentStatus === 1 &&
    !isGroupage.value &&
    canConfirm.value &&
    (!isUKOrder.value || brexitChecked.value)
  ) {
    handleConfirmOrderClick(4, "la confirmation");
    return;
  }

  // Commande groupage en attente (1 ou 9)
  if ((currentStatus === 1 || currentStatus === 9) && isGroupage.value) {
    handleConfirmOrderGroupage();
  }
};

// Gérer le changement du nombre de factures (depuis UploadZone)

// Charger les validations de la commande
const loadOrderValidations = async () => {
  if (!selectedOrderId.value) return;

  try {
    const validations = await getOrderValidations(selectedOrderId.value);

    disableAction.value = validations.disableAction;
    activeExpedi.value = validations.activeExpedi;
    // canConfirmGroupage : toujours true (la facture sera gérée dans la modal)
    canConfirmGroupage.value = true;

    // canReadyForPickup : toujours true (la facture sera gérée dans la modal)
    canReadyForPickup.value = true;
  } catch (error) {
    console.error("Erreur lors du chargement des validations:", error);
    // En cas d'erreur, on garde les valeurs par défaut (true pour permettre les actions)
  }
};

// Initialisation
const handleOrderLabelsChanged = () => {
  refreshLabelsState();
};

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    // Vérifier si l'utilisateur est un admin EFP authentifié
    const { useAdminEfpStore } = await import("@/stores/adminEfp");
    const adminEfp = useAdminEfpStore();
    const isAdminEfpAuth =
      adminEfp.isAdminEfpValidated || (await adminEfp.validate());
    if (!isAdminEfpAuth) {
      router.push({ name: "auth" });
      return;
    }
  }
  // Vérifier si un ID est passé en paramètre de route
  if (route.params.id) {
    const id = Array.isArray(route.params.id)
      ? parseInt(route.params.id[0], 10)
      : parseInt(route.params.id as string, 10);

    if (!isNaN(id)) {
      selectedOrderId.value = id;
      await loadOrderData();

      // Scroller vers le statut si demandé (depuis le bouton vert de la liste)
      if (route.query.scrollTo === "statut") {
        await nextTick();
        const el = document.getElementById("statutCommande");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  }

  window.addEventListener("orderLabelsChanged", handleOrderLabelsChanged);
});

onUnmounted(() => {
  window.removeEventListener("orderLabelsChanged", handleOrderLabelsChanged);
});

// Réagir au changement de l'ID de route (navigation SPA entre /orderdetails/:id)
watch(
  () => route.params.id,
  async (newId) => {
    if (!newId) return;
    const id = Array.isArray(newId)
      ? parseInt(newId[0], 10)
      : parseInt(newId as string, 10);
    if (!isNaN(id) && id !== selectedOrderId.value) {
      selectedOrderId.value = id;
      await loadOrderData();
    }
  },
);
</script>
